import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { DialogModule } from 'primeng/dialog';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { BesharpTableComponent, BesharpTableHeaderDefinition, BesharpTableActionDefinition } from '../../modules/besharp-table/component/besharp-table.component';
import { ProviderService } from '../../services/provider.service';
import { SidebarService } from '../../services/sidebar.service';
import { PendingSession } from '../../services/pending-sessions.service';
import { BehaviorSubject, debounceTime, distinctUntilChanged, Subscription, tap, lastValueFrom } from 'rxjs';
import { AntiMemLeak } from '../../classes/anti-mem-leak';

@Component({
  selector: 'app-pending-session',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    InputTextModule,
    ButtonModule,
    IconFieldModule,
    InputIconModule,
    DialogModule,
    TextareaModule,
    ToastModule,
    BesharpTableComponent
  ],
  templateUrl: './pending-session.component.html',
  styleUrl: './pending-session.component.scss'
})
export class PendingSessionComponent extends AntiMemLeak implements OnInit, OnDestroy {
  sessions: PendingSession[] = [];
  allSessions: PendingSession[] = [];
  totalCount = 0;
  loading = false;
  searchFilter = '';

  // Modal states
  showNotesModal = false;
  notesValue = '';
  sessionToStop: PendingSession | null = null;
  stoppingSession = false;

  // Resolution modal states
  showResolutionModal = false;
  sessionToConnect: PendingSession | null = null;
  selectedResolution: { width: number; height: number; label: string } | null = null;
  connecting = false;
  connectionAttempt = 0;
  
  resolutionOptions = [
    { width: 1920, height: 1080, label: '1920x1080 (Full HD)' },
    { width: 2560, height: 1440, label: '2560x1440 (2K)' },
    { width: 3840, height: 2160, label: '3840x2160 (4K)' },
    { width: 1280, height: 720, label: '1280x720 (HD)' },
    { width: 640, height: 480, label: '640x480 (SD)' },
  ];

  // WebRTC states
  connectedSession: PendingSession | null = null;
  webRtcLoading = false;
  webRtcError: string | null = null;

  debounceSubject = new BehaviorSubject<string>('');
  private refreshInterval: any;
  private refreshPaused = false;

  tableDefinitions: BesharpTableHeaderDefinition[] = [
    {
      def: 'id',
      title: '#',
      sortable: true,
      columnFilter: false,
    },
    {
      def: 'sessionId',
      title: 'Session ID',
      sortable: true,
      columnFilter: false,
    },
  ];

  baseActionDefinitions: BesharpTableActionDefinition[] = [
    {
      icon: 'pi pi-play',
      label: 'Connect',
      styleClass: 'p-button-outlined',
      callback: (row: PendingSession) => this.connectWithRetry(row, this.getViewportResolution()),
      tooltip: 'Connect to session',
    },
    {
      icon: 'pi pi-stop',
      label: 'Stop',
      styleClass: 'p-button-outlined p-button-danger',
      callback: (row: PendingSession) => this.stop(row),
      tooltip: 'Stop session',
    },
    {
      icon: 'pi pi-refresh',
      label: 'Renew',
      styleClass: 'p-button-outlined p-button-secondary',
      callback: (row: PendingSession) => this.renew(row),
      tooltip: 'Renew session',
    },
  ];

  constructor(
    private providerService: ProviderService,
    private router: Router,
    private messageService: MessageService,
    private sidebarService: SidebarService
  ) {
    super();
    this.subscriptions.add(
      this.debounceSubject
        .pipe(
          debounceTime(500),
          distinctUntilChanged(),
          tap((value) => this.onSearch(value)),
        )
        .subscribe(),
    );
  }

  ngOnInit() {
    this.loadSessions();
    
    this.refreshInterval = setInterval(() => {
      if (!this.refreshPaused) {
        this.loadSessions();
      }
    }, 5000);
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
  }

  loadSessions() {
    this.loading = true;
    this.subscriptions.add(this.providerService.pendingSessionsService.getAllMachinePendingSessions().subscribe({
      next: (sessions) => {
        this.allSessions = sessions;
        this.sessions = sessions;
        this.totalCount = sessions.length;
        this.loading = false;

        if (this.searchFilter.trim()) {
          this.onSearch(this.searchFilter);
        }
      },
      error: (error) => {
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load sessions: ' + (error?.message || 'Unknown error'),
          life: 3000
        });
      }
    }));
  }

  onSearch(searchTerm: string): void {
    this.applySearch(searchTerm);
  }

  private applySearch(searchTerm: string): void {
    if (searchTerm.trim()) {
      const lowerSearchTerm = searchTerm.toLowerCase();
      this.sessions = this.allSessions.filter(session =>
        session.sessionId.toLowerCase().includes(lowerSearchTerm) ||
        session.id.toLowerCase().includes(lowerSearchTerm)
      );
    } else {
      this.sessions = [...this.allSessions];
    }
  }

  sendFilterQuery(event: any): void {
    this.searchFilter = event.target.value;
    this.debounceSubject.next(this.searchFilter);
  }

  cancelResolutionSelection() {
    this.showResolutionModal = false;
    this.sessionToConnect = null;
    this.selectedResolution = null;
    this.connecting = false;
    this.connectionAttempt = 0;
    this.refreshPaused = false; 
  }

  async confirmConnect() {
    if (!this.sessionToConnect || !this.selectedResolution) return;
    
    this.connecting = true;
    this.connectionAttempt = 0;
    
    await this.connectWithRetry(this.sessionToConnect, this.selectedResolution);
  }

  async connectWithRetry(session: PendingSession, resolution: { width: number; height: number; label: string }, attempt: number = 0) {
    const maxAttempts = 10;
    const baseDelay = 1000; 
    const maxDelay = 10000;

    this.refreshPaused = true;

    this.connectionAttempt = attempt + 1;
    
    try {
      this.messageService.add({
        severity: 'info',
        summary: 'Connecting',
        detail: `Attempt ${this.connectionAttempt}/${maxAttempts}...`,
        life: 3000
      });

      this.sidebarService.collapseSidebar();
      
      const creds = await lastValueFrom(this.providerService.pendingSessionsService.getMachineCredentials(session.sessionId));
      this.providerService.webRtcService.setSessionCredentials(session.sessionId, creds);

      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Connected successfully! Redirecting...',
        life: 2500
      });

      this.connecting = false;
      this.showResolutionModal = false;
      this.refreshPaused = false;
      this.router.navigate(['/app/connection/', session.sessionId], {
        queryParams: {
          width: resolution.width,
          height: resolution.height
        }
      });
    } catch (error: any) {
      console.log('Connection attempt failed:', error);
      const statusCode = error?.status;
      const isRetryableError = statusCode === 502 || statusCode === 504 || statusCode === 405;
      
      if (isRetryableError && attempt < maxAttempts - 1) {
        const delay = Math.min(baseDelay * Math.pow(2, attempt), maxDelay);
        
        this.messageService.add({
          severity: 'info',
          summary: 'Connecting',
          detail: 'Connection is still being set up. Retrying in ' + (delay / 1000) + ' seconds...',
          life: delay
        });
        
        await new Promise(resolve => setTimeout(resolve, delay));
        
        await this.connectWithRetry(session, resolution, attempt + 1);
      } else {
        this.messageService.add({
          severity: 'error',
          summary: 'Connection Error',
          detail: `Failed to connect: ${error?.message || 'Unknown error'} (Status: ${statusCode || 'N/A'})`,
          life: 5000
        });
        this.connecting = false;
        this.connectionAttempt = 0;
        this.refreshPaused = false;
      }
    }
  }

  async connect(session: PendingSession) {
    this.connectedSession = session;
    this.messageService.add({
      severity: 'info',
      summary: 'Connecting',
      detail: 'Connecting, please wait...',
      life: 3000
    });

    this.sidebarService.collapseSidebar();

    try {
      this.webRtcLoading = true;
      const creds = await lastValueFrom(this.providerService.pendingSessionsService.getMachineCredentials(session.sessionId));
      this.providerService.webRtcService.setSessionCredentials(session.sessionId, creds);

      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Connected successfully! Redirecting...',
        life: 2500
      });

      this.router.navigate(['/app/connection/', session.sessionId]);
      this.webRtcLoading = false;
    } catch (error: any) {
      this.messageService.add({
        severity: 'error',
        summary: 'Connection Error',
        detail: 'Failed to connect: ' + (error?.message || 'Unknown error'),
        life: 4000
      });
      this.webRtcLoading = false;
    }
  }

  stop(session: PendingSession) {
    this.sessionToStop = session;
    this.notesValue = '';
    this.showNotesModal = true;
  }

  confirmStop() {
    if (this.sessionToStop && this.notesValue.trim()) {
      this.stoppingSession = true;
      this.providerService.pendingSessionsService.stopSession(
        this.sessionToStop.id,
        this.sessionToStop.sessionId,
        this.notesValue
      ).subscribe({
        next: () => {
          this.loadSessions();
          this.showNotesModal = false;
          this.sessionToStop = null;
          this.notesValue = '';
          this.stoppingSession = false;
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Stop command sent...',
            life: 2500
          });
        },
        error: (error: any) => {
          this.stoppingSession = false;
          this.showNotesModal = false;
          this.sessionToStop = null;
          this.notesValue = '';
          let reason = error?.error?.message || error?.message || error?.statusText || 'Unknown error';
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to send stop command: ' + reason,
            life: 4000
          });
        }
      });
    }
  }

  cancelStop() {
    this.showNotesModal = false;
    this.sessionToStop = null;
    this.notesValue = '';
  }

  renew(session: PendingSession) {
    this.loading = true;
    this.sessions = [];
    this.providerService.pendingSessionsService.renewSession(session.id, session.sessionId).subscribe({
      next: () => {
        this.loadSessions();
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Renew command sent...',
          life: 2500
        });
      },
      error: (error: any) => {
        this.loading = false;
        this.sessions = [...this.allSessions];
        let reason = error?.error?.message || error?.message || error?.statusText || 'Unknown error';
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to send renew command: ' + reason,
          life: 4000
        });
      }
    });
  }

  onRowClicked(row: PendingSession): void {
    console.log('Row clicked:', row);
  }

  private getViewportResolution(): { width: number; height: number; label: string } {
    const width = Math.max(640, Math.floor(window.innerWidth));
    const height = Math.max(480, Math.floor(window.innerHeight));
    return { width, height, label: `Auto ${width}x${height}` };
  }
}
